import React, { useEffect, useState } from 'react';

import Button from '../atoms/Button';
import Box from '../atoms/Box';
import Text from '../atoms/Text';

import { useTranslate } from '../../translate/I18n';
import { assetize } from '../../utils/pureUtils';

import htmlizeCarriageReturn from '../htmlizeCarriageReturn';
import styled from 'styled-components';
import { retinaImage } from 'polished';
import CopyrightCalculatorForm from './CopyrightCalculatorForm';
import CopyrightCalculatorResults from './CopyrightCalculatorResults';

import { calculateCopyrightAdvantage } from '../../store/ulaw/ulaw.service';
import { LANGUAGES } from '../../constants/index';

import FaqQuestions from '../faq/FaqQuestions';
import { getQuestionsByCategoryFromNamespace } from '../faq/faqUtils';
import { handle, toastMainError } from '../../infrastructure/validation/error';
import { toast } from 'react-toastify';
import Banner from '../atoms/Banner';

const StyledCardContainer = styled('div')(({ theme }) => ({
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100%',
  maxWidth: '1277px',
  backgroundPosition: 'center 250px',
  padding: `${theme.space[0]} ${theme.space[2]}px`,
  margin: 'auto',
  [`@media (max-width: ${theme.breakpoints.lg})`]: {
    backgroundPosition: 'center 12%'
  },
  ...retinaImage(
    assetize('/illustrations/creative-objects'),
    '100%',
    'png',
    assetize('/illustrations/creative-objects@2x'),
    '@2x'
  )
}));

const StyledWorkingPeopleImg = styled('img')(() => ({
  width: '100%',
  maxWidth: '1368px',
  display: 'block',
  margin: '70px auto 102px'
}));

const {
  LOCIZE: { ENGLISH, DUTCH }
} = LANGUAGES;

const CopyrightCalculator = () => {
  const { t, i18n } = useTranslate(['copyrightCalculator']);
  const [resultsCalculation, setResultsCalculation] = useState();
  const lang = i18n.language;

  const questionsByCategory = getQuestionsByCategoryFromNamespace(t, 'copyrightCalculator:faq');

  const ulawLinkBase = 'https://guide-gratuit.ulaw.be/partenaire-juridique-simulator';

  const fullUlawLink = () => {
    return ulawLinkBase + (lang === ENGLISH || lang === DUTCH ? `-${lang}` : '');
  };

  const defaultValues = {
    annualGrossIncome: 85000,
    allowedTimeCreativeWork: 20
  };

  const calculateCopyright = async (allowedTimeCreativeWork, annualGrossIncome) => {
    try {
      const results = await calculateCopyrightAdvantage(
        parseFloat(allowedTimeCreativeWork) || 0,
        parseFloat(annualGrossIncome) || 0
      );
      setResultsCalculation(results);
    } catch (error) {
      handle(error).with(toastMainError(toast.error, t));
    }
  };

  useEffect(() => {
    calculateCopyright(defaultValues.allowedTimeCreativeWork, defaultValues.annualGrossIncome);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues.allowedTimeCreativeWork, defaultValues.annualGrossIncome]);

  return (
    <Box>
      <StyledCardContainer>
        <Box mb={2} mt={4}>
          <Banner name="displayCopyrightCalculatorWarningMessage" description={t('copyrightCalculator:amendments')} />
        </Box>
        <Box maxWidth="1028px" mx="auto" pt={6} pb={10} textAlign="center">
          <Text as="h1" styleName="wH2" mb={2}>
            {t('copyrightCalculator:title1')}
          </Text>
          <Text styleName="wPBig">{htmlizeCarriageReturn(t('copyrightCalculator:subtitle1'))}</Text>
        </Box>
        <Box maxWidth="1028px" mx="auto">
          <Box display="flex" flexDirection={{ _: 'column', lg: 'row' }}>
            <CopyrightCalculatorForm calculateCopyrightAdvantage={calculateCopyright} defaultValues={defaultValues} />
            <CopyrightCalculatorResults resultsCalculation={resultsCalculation} />
          </Box>

          <Box textAlign="center" px={3}>
            <Text as="h2" styleName="wH3" fontWeight="800" mt={7} mb={3}>
              {t('copyrightCalculator:title2')}
            </Text>
            <Text styleName="wPBig" mb={3}>
              {t('copyrightCalculator:subtitle2')}
            </Text>
            <a href={fullUlawLink()} target="_blank" rel="noopener noreferrer">
              <Button shape="rounded" mb={9} data-cy='btn-ulaw-apply'>
                {t('copyrightCalculator:takeAdvantage')}
              </Button>
            </a>
          </Box>
        </Box>

        <FaqQuestions questionsByCategory={questionsByCategory} />

        <StyledWorkingPeopleImg
          alt="people working in a working room"
          src={assetize('/illustrations/working-room.png')}
          srcSet={assetize('/illustrations/working-room@2x.png 2x')}
        />

        <Text maxWidth="820px" margin="auto" styleName="wPSmall" mb={14}>
          {htmlizeCarriageReturn(t('copyrightCalculator:legal'))}
        </Text>
      </StyledCardContainer>
    </Box>
  );
};

export default CopyrightCalculator;
